/** Authentication tab — password (set + reset link), MFA device management and,
 *  for system users, the console invite (folded in from the old Invitations tab).
 *  Shown for system + staff users (guests authenticate via magic links).
 *
 *  The MFA section also says how many recovery codes are left and, for staff, whether the
 *  dashboard's password sign-in can ask them for a second factor and what their tenant's
 *  dashboard two-step sign-in is (dashboard password MFA plan §3.13). */
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import { dashboardMfaPolicyLabel } from '@/api/dashboardMfa';
import * as usersApi from '@/api/users';
import type { ConsoleUserDetail, MFADevice, MFAOverview, ResetChannel } from '@/api/users';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useSnackbar } from '@/components/Snackbar';
import { usePermissions } from '@/hooks/usePermissions';

import { MfaEnrollDialog } from './MfaEnrollDialog';

interface Props {
  user: ConsoleUserDetail;
  onChanged: () => void;
}

export function AuthenticationTab({ user, onChanged }: Props) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const { hasPerm } = usePermissions();
  const canPassword = hasPerm('console_user_password');
  const canMfa = hasPerm('console_user_mfa_manage');

  const [pwOpen, setPwOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [pwErrors, setPwErrors] = useState<string[]>([]);
  const [resetChannel, setResetChannel] = useState<ResetChannel>('email');
  const [enrollKind, setEnrollKind] = useState<MFADevice['kind'] | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<MFADevice | null>(null);

  const mfaQuery = useQuery({
    queryKey: ['userMfa', user.id],
    queryFn: () => usersApi.mfaOverview(user.id),
    enabled: canMfa,
  });

  const setPasswordMutation = useMutation({
    mutationFn: () => usersApi.setPassword(user.id, password),
    onSuccess: (data) => {
      snackbar.show(`Password set — revoked ${data.tokens_revoked} session(s).`, 'success');
      setPwOpen(false);
      setPassword('');
      setPwErrors([]);
    },
    onError: (e: unknown) => {
      if (e instanceof ConsoleApiError && Array.isArray((e.body as { errors?: string[] })?.errors)) {
        setPwErrors((e.body as { errors: string[] }).errors);
      } else {
        snackbar.show(e instanceof ConsoleApiError ? e.message : 'Could not set password.', 'error');
      }
    },
  });

  const resetMutation = useMutation({
    mutationFn: () => usersApi.sendPasswordReset(user.id, resetChannel),
    onSuccess: (data) => snackbar.show(`Reset link sent via ${data.channel}.`, 'success'),
    onError: (e: unknown) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Could not send link.', 'error'),
  });

  const removeDevice = useMutation({
    mutationFn: (d: MFADevice) => usersApi.mfaDeleteDevice(user.id, d.id, d.kind),
    onSuccess: () => {
      snackbar.show('Device removed.', 'success');
      qc.invalidateQueries({ queryKey: ['userMfa', user.id] });
      onChanged();
    },
  });

  const setPrimary = useMutation({
    mutationFn: (d: MFADevice) => usersApi.mfaSetPrimary(user.id, d.id, d.kind),
    onSuccess: () => {
      snackbar.show('Primary method updated.', 'success');
      qc.invalidateQueries({ queryKey: ['userMfa', user.id] });
    },
  });

  const overview = mfaQuery.data;

  return (
    <Stack spacing={4} sx={{ maxWidth: 640 }}>
      {/* Password */}
      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Password
        </Typography>
        {canPassword ? (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button variant="outlined" onClick={() => setPwOpen(true)}>
              Set password directly
            </Button>
            <Divider orientation="vertical" flexItem />
            <TextField
              select
              size="small"
              label="Channel"
              value={resetChannel}
              onChange={(e) => setResetChannel(e.target.value as ResetChannel)}
              sx={{ width: 120 }}
            >
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="sms">SMS</MenuItem>
            </TextField>
            <Button
              variant="outlined"
              onClick={() => resetMutation.mutate()}
              disabled={resetMutation.isPending}
            >
              Send reset link
            </Button>
          </Stack>
        ) : (
          <Alert severity="info">You do not have permission to manage passwords.</Alert>
        )}
      </Box>

      <Divider />

      {/* MFA */}
      <Box>
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Multi-factor authentication
          </Typography>
          {overview?.primary_method && (
            <Chip size="small" label={`Primary: ${overview.primary_method}`} color="primary" />
          )}
        </Stack>

        {user.kind === 'staff' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            The dashboard asks for an authenticator-app or SMS code at password sign-in (e-mail
            devices are not used there).
          </Alert>
        )}

        {!canMfa ? (
          <Alert severity="info">You do not have permission to manage MFA devices.</Alert>
        ) : mfaQuery.isLoading ? (
          <Typography variant="body2">Loading…</Typography>
        ) : (
          <>
            {overview && <MfaFacts user={user} overview={overview} />}
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Button size="small" variant="outlined" onClick={() => setEnrollKind('totp')}>
                Enroll TOTP
              </Button>
              <Button size="small" variant="outlined" onClick={() => setEnrollKind('sms')}>
                Enroll SMS
              </Button>
              <Button size="small" variant="outlined" onClick={() => setEnrollKind('email')}>
                Enroll Email
              </Button>
            </Stack>

            {overview && overview.devices.length === 0 ? (
              <Alert severity="warning">No MFA devices enrolled.</Alert>
            ) : (
              <List dense>
                {overview?.devices.map((d) => {
                  const isPrimary = overview.primary_method === d.kind;
                  return (
                    <ListItem
                      key={`${d.kind}-${d.id}`}
                      divider
                      secondaryAction={
                        <Stack direction="row" spacing={0.5}>
                          {!isPrimary && (
                            <Button size="small" onClick={() => setPrimary.mutate(d)}>
                              Make primary
                            </Button>
                          )}
                          <Button size="small" color="error" onClick={() => setConfirmRemove(d)}>
                            Remove
                          </Button>
                        </Stack>
                      }
                    >
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <span>
                              {d.kind.toUpperCase()} — {d.name}
                            </span>
                            {isPrimary && <StarIcon fontSize="inherit" color="primary" />}
                          </Stack>
                        }
                        secondary={d.target_masked || (d.confirmed ? 'Confirmed' : 'Unconfirmed')}
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </>
        )}
      </Box>

      {user.kind === 'system' && (
        <>
          <Divider />
          <InvitationSection user={user} />
        </>
      )}

      {/* Set password dialog */}
      <Dialog open={pwOpen} onClose={() => setPwOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Set password for {user.email}</DialogTitle>
        <DialogContent>
          <TextField
            type="password"
            fullWidth
            label="New password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ mt: 1 }}
            autoComplete="new-password"
          />
          {pwErrors.length > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {pwErrors.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            All of the user&apos;s active sessions will be revoked.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPwOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!password || setPasswordMutation.isPending}
            onClick={() => {
              setPwErrors([]);
              setPasswordMutation.mutate();
            }}
          >
            Set password
          </Button>
        </DialogActions>
      </Dialog>

      {enrollKind && (
        <MfaEnrollDialog
          userId={user.id}
          kind={enrollKind}
          defaultEmail={user.email}
          open
          onClose={() => setEnrollKind(null)}
          onEnrolled={() => {
            setEnrollKind(null);
            qc.invalidateQueries({ queryKey: ['userMfa', user.id] });
            onChanged();
          }}
        />
      )}

      {confirmRemove && (
        <ConfirmDialog
          open
          title="Remove MFA device?"
          message={`Remove the ${confirmRemove.kind.toUpperCase()} device "${confirmRemove.name}"?`}
          confirmLabel="Remove"
          destructive
          onConfirm={() => {
            removeDevice.mutate(confirmRemove);
            setConfirmRemove(null);
          }}
          onCancel={() => setConfirmRemove(null)}
        />
      )}
    </Stack>
  );
}

/** Recovery codes left (one store for console and dashboard codes) and, for staff (the
 *  overview's dashboard fields are null for a system user), whether the dashboard's password
 *  sign-in can ask for a second factor and the tenant's effective dashboard two-step sign-in.
 *  A backend older than these fields leaves them out: nothing is shown for them then. */
function MfaFacts({ user, overview }: { user: ConsoleUserDetail; overview: MFAOverview }) {
  const { hasPerm } = usePermissions();
  const codes = overview.recovery_codes_remaining;
  const policy = overview.dashboard_mfa_policy;
  const tenant = user.tour_operator;
  const mustEnroll = overview.dashboard_enrolled === false && policy?.mode === 'mandatory';
  return (
    <Stack spacing={0.5} sx={{ mb: 2 }}>
      {codes != null && (
        <Typography variant="body2">
          Recovery codes: {codes === 0 ? 'none left' : `${codes} unused`}
        </Typography>
      )}
      {overview.dashboard_enrolled != null && (
        <Typography variant="body2">
          Dashboard two-step sign-in:{' '}
          {overview.dashboard_enrolled
            ? 'set up (authenticator app or SMS)'
            : mustEnroll
              ? 'not set up — required at their next password sign-in'
              : 'not set up'}
        </Typography>
      )}
      {policy && (
        <Typography variant="body2">
          Tenant policy:{' '}
          {tenant && hasPerm('console_top_view') ? (
            <Link component={RouterLink} to={`/tour-operators/${tenant.id}/sign-in-security`}>
              {dashboardMfaPolicyLabel(policy)}
            </Link>
          ) : (
            dashboardMfaPolicyLabel(policy)
          )}
        </Typography>
      )}
    </Stack>
  );
}

/** Console invites exist for system users only (`pending_invite_count` is null
 *  for the other kinds). There is no per-user invite endpoint: the count says
 *  whether a usable invite is out, and re-issuing goes through the batch
 *  `resend_invite` — a background task that revokes any unused invite first
 *  (`console_tokens.revoke_pending`), so exactly one valid invite remains. */
/** The invitation section's DOM id — where an old `/users/<id>/invitations` link lands. */
export const INVITATION_ANCHOR_ID = 'user-invitation';

function InvitationSection({ user }: { user: ConsoleUserDetail }) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const { hasPerm } = usePermissions();
  const [error, setError] = useState<string | null>(null);
  const pending = user.pending_invite_count ?? 0;

  const resend = useMutation({
    mutationFn: () => usersApi.batch('resend_invite', [user.id]),
    onSuccess: () => {
      snackbar.show('Invite re-issue queued. Prior invites for this user are revoked.', 'success');
      // Prefix match: the detail query (pending count) and the users grids.
      qc.invalidateQueries({ queryKey: ['consoleUsers'] });
    },
    onError: (e: unknown) =>
      setError(e instanceof ConsoleApiError ? e.message : 'Could not re-issue the invite.'),
  });

  return (
    <Box
      component="section"
      id={INVITATION_ANCHOR_ID}
      tabIndex={-1}
      aria-labelledby={`${INVITATION_ANCHOR_ID}-title`}
      sx={{
        outline: 'none',
        borderRadius: 1,
        '&[data-revealed]:focus': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` },
      }}
    >
      <Typography id={`${INVITATION_ANCHOR_ID}-title`} variant="h6" sx={{ mb: 1 }}>
        Invitation
      </Typography>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        {pending === 0
          ? 'No pending invite.'
          : pending === 1
            ? 'Invite pending: sent and not used yet.'
            : `${pending} invites pending: sent and not used yet.`}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Re-issuing revokes any unused invite for this user and emails a fresh link.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}
      {hasPerm('console_user_invite') && (
        <Button
          variant="outlined"
          onClick={() => {
            setError(null);
            resend.mutate();
          }}
          disabled={resend.isPending}
        >
          Re-issue invite
        </Button>
      )}
    </Box>
  );
}
